'use client';

import { Cash } from '@shared/icons';
import { twMerge } from '@shared/themes';
import { NumberFormats, useCurrencyFormatter } from '@shared/utils';
import { useTranslations } from 'next-intl';
import type { ComponentProps } from 'react';

type TotalWinningsTitleKey = 'title' | 'prize';

type TotalWinningsProps = ComponentProps<'div'> & {
  calculating: boolean;
  totalWinnings?: number;
  titleKey?: TotalWinningsTitleKey;
};

function TotalWinnings({ calculating, totalWinnings, titleKey = 'title', className, ...props }: TotalWinningsProps) {
  const t = useTranslations('lotteries.ticket_details.your_numbers_section.total_winnings');
  const currencyFormatter = useCurrencyFormatter();

  const getAmountTextColor = () => {
    if (calculating) return 'text-foreground';
    if (totalWinnings && totalWinnings > 0) return 'text-success-foreground';
    return 'text-foreground-subtle';
  };

  const classes = {
    container: twMerge('flex flex-col gap-1 py-5 border-y-1 border-border', className),
    title: 'paragraph-l-strong text-foreground',
    amount: twMerge('paragraph-strong', getAmountTextColor()),
    icon: twMerge(
      'flex items-center justify-center rounded-full size-7 ',
      totalWinnings && totalWinnings > 0 ? ' bg-success-background' : 'bg-button-background-inverse-hover',
      getAmountTextColor()
    ),
  };

  const formattedWinnings = calculating
    ? t('calculating')
    : currencyFormatter(totalWinnings ?? 0)
        .format(NumberFormats.WITH_DECIMALS)
        .get();

  const winningsIcon = !calculating ? (
    <span className={classes.icon} data-testid="total-winnings-icon">
      <Cash size="small" aria-hidden />
    </span>
  ) : null;

  return (
    <div className={classes.container} data-testid="total-winnings" {...props}>
      <div className="flex items-center justify-between gap-4">
        <p className={classes.title} data-testid="total-winnings-title">
          {t(titleKey)}
        </p>
        <div className="flex items-center gap-2">
          {winningsIcon}
          <p className={classes.amount} data-testid="total-winnings-amount">
            {formattedWinnings}
          </p>
        </div>
      </div>
      {calculating && (
        <p className="paragraph-s text-foreground-subtle" data-testid="total-winnings-message">
          {t('calculating_message')}
        </p>
      )}
    </div>
  );
}

export { TotalWinnings };
export type { TotalWinningsProps, TotalWinningsTitleKey };
